// ── SEOHead ───────────────────────────────────────────────────────────────────
// Injects per-page SEO tags: canonical, hreflang, og:locale, and noindex guard.
// Mount once inside <BrowserRouter> (requires useLocation).

import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const BASE = "https://atelier-ritual.ca";

const LOCALE_MAP: Record<string, string> = {
  en: "en_CA",
  fr: "fr_CA",
  es: "es_419",
};

const SEOHead = () => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();

  const lang = i18n.language.split("-")[0];
  const url = `${BASE}${pathname}`;
  const ogLocale = LOCALE_MAP[lang] ?? "en_CA";
  const isProduction =
    window.location.hostname === "atelier-ritual.ca" ||
    window.location.hostname === "www.atelier-ritual.ca";

  return (
    <Helmet>
      {/* Dynamic language attribute on <html> */}
      <html lang={lang} />

      {/* Self-referential canonical — overrides Vercel preview URLs */}
      <link rel="canonical" href={url} />

      {/* hreflang — all language variants share the same URL structure */}
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="fr" href={url} />
      <link rel="alternate" hrefLang="es" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Reflect active language in OG tags */}
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={ogLocale} />

      {/* Block indexing on Vercel preview deployments and local dev */}
      {!isProduction && <meta name="robots" content="noindex, nofollow" />}
    </Helmet>
  );
};

export default SEOHead;
